import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { FlagsProvider } from 'flagged'
import MobileNavModal from 'shared/mobile-nav-modal'
import StoreSearchButton from 'store/components/search-button'
import CartSummary from 'cart/components/cart-summary'
import classNames from 'classnames'
import { mapKeys, camelCase } from 'lodash'
import {
  faqPath,
  findPath,
  healthPath,
  helloBabyPath,
  indexPath,
  newRegistryPath,
  registryHomePath,
  registryPath,
  storePath,
} from 'lib/urls'
import { initializePurchasingStore } from 'cart/lib'
import css from './global-nav.scss'
import {
  ACTIVE_NAV_SECTION_EVENT,
  HIDE_MOBILE_NAV_MODAL_EVENT,
} from 'shared/constants'
import Link from 'components/link'
import AccountMenu from './components/AccountMenu'
import SiteNavSection from './components/SiteNavSection'
import { track, withContextualizedTracking, useTracking } from 'lib/analytics'

@withContextualizedTracking((props) => {
  // use to track cart opened eventLocation
  const activeNavItem = props.activeNavItem
  var eventLocation = (function (eventLocation) {
    switch (eventLocation) {
      case 'Home':
        return track.EventLocation.HOME_PAGE
      case 'Registry':
        return track.EventLocation.REGISTRY
      case 'Guides':
        return track.EventLocation.GUIDE
      case 'Store':
        return track.EventLocation.STORE
      default:
        return track.EventLocation.NONE
    }
  })(activeNavItem)
  return { eventLocation: eventLocation }
})
class GlobalNav extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      activeNavItem: props.activeNavItem,
      sideNavVisible: false,
      currentUser: props.currentUser,
    }
    this.handleToggleNav = this.handleToggleNav.bind(this)
  }

  componentDidMount() {
    PubSub.subscribe(HIDE_MOBILE_NAV_MODAL_EVENT, () => {
      this.setState({ sideNavVisible: false })
    })
    PubSub.subscribe(ACTIVE_NAV_SECTION_EVENT, (event) => {
      this.setState({ activeNavItem: event })
    })
    PubSub.subscribe('switchNavState', (user) => {
      const formattedUser = mapKeys(user, (_, key) => camelCase(key))
      this.setState({ currentUser: formattedUser })
    })
  }

  handleToggleNav() {
    this.setState({
      sideNavVisible: !this.state.sideNavVisible,
    })
  }

  render() {
    const {
      currentCart,
      featureFlags,
      hideCartSummary,
      isRegistryGuestView,
      showMinimalView,
      showAcquisitionView,
    } = this.props

    const { activeNavItem, currentUser } = this.state

    return (
      <FlagsProvider features={featureFlags}>
        <nav className={classNames(css.globalNav, css.withShadow)}>
          <div className="container">
            <div className={css.flexRow}>
              <ul className={classNames(css.farLeft)}>
                {!showAcquisitionView && (
                  <ToggleNav onClick={this.handleToggleNav} />
                )}
                <SiteNavSection
                  showMinimalView={isRegistryGuestView || showMinimalView}
                  user={currentUser}
                  activeNavItem={activeNavItem}
                  showAcquisitionView={showAcquisitionView}
                />
              </ul>

              <div className={classNames(css.middle)}>
                <Logo user={currentUser} />
              </div>

              <ul className={css.farRight}>
                <ActionsNavSection
                  showAcquisitionView={showAcquisitionView}
                  currentUser={currentUser}
                  showMinimalView={isRegistryGuestView || showMinimalView}
                  showSearch={activeNavItem === 'Guides'}
                  cart={currentCart}
                  isLoggedIn={currentUser ? true : false}
                  hideCartSummary={hideCartSummary}
                />
              </ul>
              <MobileNavModal
                {...this.props}
                show={this.state.sideNavVisible}
                onHide={this.handleToggleNav}
              />
            </div>
          </div>
        </nav>
      </FlagsProvider>
    )
  }
}

GlobalNav.propTypes = {
  activeNavItem: PropTypes.string,
  currentCart: PropTypes.object,
  currentUser: PropTypes.object,
  showMinimalView: PropTypes.bool,
  userCanViewRegistry: PropTypes.bool,
}

const ActionsNavSection = ({
  cart,
  currentUser,
  hideCartSummary,
  showMinimalView,
  isLoggedIn,
  showSearch,
  showAcquisitionView,
}) => {
  const reservations = cart.reservations.map((res) =>
    initializePurchasingStore(res)
  )
  return (
    <>
      {!showAcquisitionView && (
        <li className={classNames(css.navLink, 'hidden-xs')}>
          <Link url={faqPath} newWindow>
            Help
          </Link>
        </li>
      )}

      {!showMinimalView &&
        (isLoggedIn ? (
          <li className={classNames(css.navLink)}>
            <AccountMenu user={currentUser} />
          </li>
        ) : (
          // logged out
          <>
            <li className={classNames('hidden-xs', css.navLink)}>
              <a href="javascript:PubSub.publish ('authModal.show')">Log In</a>
            </li>
            <li
              className={classNames(
                'hidden-xs btn-pill btn btn-sm btn-info',
                css.navLink,
                css.navButton
              )}
            >
              <a href={newRegistryPath}>Sign Up</a>
            </li>
          </>
        ))}

      {showSearch && (
        <li className={classNames(css.navIcnBtn, 'hidden-md', 'visible-xs')}>
          <StoreSearchButton />
        </li>
      )}

      {!showAcquisitionView && (
        <li className={css.navIcnBtn} style={{ position: 'relative' }}>
          <CartSummary
            cart={cart.cart}
            disableDropdown={hideCartSummary}
            registryUrlSlug={cart.registryUrlSlug}
            reservations={reservations}
          />
        </li>
      )}
    </>
  )
}

ActionsNavSection.propTypes = {
  cart: PropTypes.object,
  showMinimalView: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  showSearch: PropTypes.bool,
  showAcquisitionView: PropTypes.bool,
}

const Logo = ({ className, user }) => (
  <div className={classNames(css.logo, className)}>
    <a href={user ? registryHomePath : indexPath}>
      Babylist - Universal Baby Registry
    </a>
  </div>
)

Logo.propTypes = {
  className: PropTypes.string,
  user: PropTypes.object,
}

const ToggleNav = ({ onClick }) => (
  <button
    className={classNames(css.navToggle, 'hidden-md', 'visible-xs')}
    type="button"
    onClick={onClick}
  >
    <span className="sr-only">Toggle navigation</span>
    <span className={css.iconBar}></span>
    <span className={css.iconBar}></span>
    <span className={css.iconBar}></span>
  </button>
)

ToggleNav.propTypes = {
  onClick: PropTypes.func,
}

export {
  GlobalNav as default,
  SiteNavSection,
  ActionsNavSection,
  Logo,
  ToggleNav,
}
